import { Component, ElementRef, HostListener, Injectable, QueryList, ViewChildren, afterNextRender, computed, inject, signal } from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from "@angular/material/dialog";
import { lastValueFrom } from "rxjs";
import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent,
} from "@tilby/tilby-ui-lib/components/tilby-dialog";
import { TilbyMessageDetail } from "tilby-models";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";
import { TranslateModule } from "@ngx-translate/core";
import { SafeUrlPipe } from 'src/app/pipes/safe-url.pipe';

export type ShowTilbyMessagesDialogData = { messages: TilbyMessageDetail[] };

/** Returns the message when it is made only of a url (to be shown in an iframe), otherwise an empty string */
function getOnlyUrl(message: string): string {
    const trimmed = message.trim();
    const match = trimmed.match(/(https?:\/\/[^\s]+)/i);

    return match && match[0] === trimmed ? message : '';
}

@Component({
    selector: "app-tilby-dialog",
    standalone: true,
    imports: [
        MatButtonModule,
        MatCheckboxModule,
        MatDialogModule,
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        TranslateModule,
        SafeUrlPipe
    ],
    templateUrl: "./show-tilby-messages-dialog.component.html",
    styleUrls: ["./show-tilby-messages-dialog.component.scss"],
})
export class ShowTilbyMessagesDialogComponent {
    private readonly data: ShowTilbyMessagesDialogData = inject(MAT_DIALOG_DATA);
    private readonly matDialogRef = inject(MatDialogRef);
    private readonly hiddenIds = signal<Set<number>>(new Set());
    @ViewChildren('slide') private slides!: QueryList<ElementRef<HTMLElement>>;

    protected readonly messages = this.data.messages;
    protected readonly urls = this.messages.map(m => getOnlyUrl(m.message || ''));
    protected readonly hasUrl = this.urls.some(url => !!url);
    protected readonly index = signal(0);
    protected readonly slideHeight = signal<number | null>(null);
    protected readonly current = computed(() => this.messages[this.index()]);
    protected readonly isFirst = computed(() => this.index() === 0);
    protected readonly isLast = computed(() => this.index() === this.messages.length - 1);
    protected readonly isHidden = computed(() => this.hiddenIds().has(this.current()?.id as number));
    /** Url messages need a wide dialog, text ones a narrower one; the css transition animates the change */
    protected readonly width = computed(() => {
        if (!this.hasUrl) {
            return null;
        }

        return this.urls[this.index()] ? '80vw' : 'min(500px, 80vw)';
    });

    constructor() {
        afterNextRender(() => this.updateHeight());
    }

    /** Sets the container height to the active slide one, the css transition animates the change */
    @HostListener('window:resize')
    protected updateHeight() {
        const slide = this.slides?.get(this.index());
        this.slideHeight.set(slide ? slide.nativeElement.offsetHeight : null);
    }

    protected setHidden(hide: boolean) {
        const next = new Set(this.hiddenIds());
        const id = this.current().id as number;

        if (hide) {
            next.add(id);
        } else {
            next.delete(id);
        }

        this.hiddenIds.set(next);
    }

    protected prev() {
        this.goTo(this.index() - 1);
    }

    protected next() {
        this.goTo(this.index() + 1);
    }

    private goTo(i: number) {
        this.index.set(Math.min(Math.max(0, i), this.messages.length - 1));
        this.updateHeight();
    }

    /** Goes to the next message, or closes the dialog on the last one */
    protected ok() {
        if (this.isLast()) {
            this.confirm();
        } else {
            this.next();
        }
    }

    /** Closes the dialog returning the ids of the messages the user chose not to see again */
    private confirm() {
        this.matDialogRef.close([...this.hiddenIds()]);
    }
}

@Injectable({
    providedIn: "root",
})
export class ShowTilbyMessagesDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    /** Resolves with the ids of the messages the user chose not to see again */
    public openDialog(config?: NonNullableConfigData<ShowTilbyMessagesDialogData>): Promise<number[] | undefined> {
        const data: ShowTilbyMessagesDialogData = config?.data || { messages: [] };

        // nothing to show: the dialog cannot render an empty list
        if (!data.messages?.length) {
            return Promise.resolve([]);
        }

        const configEdited: NonNullableConfigData<ShowTilbyMessagesDialogData> = {
            ...config,
            ...this.switchMobileDesktopDimensions(),
            disableClose: true,
            data,
        };
        return lastValueFrom(
            this.dialogRef.open(ShowTilbyMessagesDialogComponent, configEdited).afterClosed()
        );
    }
}
